import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { selectAllPages } from '../../utils/chunkedIn';

interface Stats {
  totalStudents: number;
  newStudentsThisMonth: number;
  totalPrograms: number;
  activePrograms: number;
  totalRevenue: number;
  pendingPayments: number;
  refundedAmount: number;
  registeredCount: number;
  waitingCount: number;
  cancelledCount: number;
  programStats: { name: string; count: number; revenue: number }[];
}

const Statistics: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      // 집계는 전체를 다 봐야 한다. PostgREST 1,000행 상한에 걸리면 통계가 조용히 틀린다.
      const { data: applications, error: applicationsError } = await selectAllPages<any>((from, to) => supabase
        .from('applications')
        .select(`
          id,
          student_id,
          status,
          payment_status,
          payment_amount,
          paid_at,
          created_at,
          program_classes:class_id (
            program_id,
            programs:program_id (
              name
            )
          )
        `)
        .order('id')
        .range(from, to));

      if (applicationsError) console.error('Error loading applications for statistics:', applicationsError);

      const { data: programs } = await supabase
        .from('programs')
        .select('id, name, status');

      const { data: refunds } = await supabase
        .from('refunds')
        .select('refund_amount, status')
        .eq('status', 'completed');

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const uniqueStudents = new Set(applications?.map(a => a.student_id) || []);

      const recentApplications = applications?.filter(a =>
        new Date(a.created_at) >= thirtyDaysAgo
      ) || [];
      const newStudents = new Set(recentApplications.map(a => a.student_id));

      const paidApplications = applications?.filter(a => a.payment_status === 'completed') || [];
      const totalRevenue = paidApplications.reduce((sum, a) => sum + (a.payment_amount || 0), 0);

      const pendingApplications = applications?.filter(a => a.payment_status === 'pending') || [];
      const pendingPayments = pendingApplications.reduce((sum, a) => sum + (a.payment_amount || 0), 0);

      const refundedAmount = refunds?.reduce((sum, r) => sum + (r.refund_amount || 0), 0) || 0;

      const registeredCount = applications?.filter(a => a.status === 'registered').length || 0;
      const waitingCount = applications?.filter(a => a.status === 'waiting').length || 0;
      const cancelledCount = applications?.filter(a => a.status === 'cancelled').length || 0;

      const programMap = new Map<string, { count: number; revenue: number }>();
      applications?.forEach(app => {
        const programName = (app.program_classes as any)?.programs?.name || '알 수 없음';
        const current = programMap.get(programName) || { count: 0, revenue: 0 };
        current.count += 1;
        if (app.payment_status === 'completed') {
          current.revenue += app.payment_amount || 0;
        }
        programMap.set(programName, current);
      });

      const programStats = Array.from(programMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const activePrograms = programs?.filter(p =>
        p.status === 'recruiting' || p.status === 'ongoing'
      ).length || 0;

      setStats({
        totalStudents: uniqueStudents.size,
        newStudentsThisMonth: newStudents.size,
        totalPrograms: programs?.length || 0,
        activePrograms,
        totalRevenue,
        pendingPayments,
        refundedAmount,
        registeredCount,
        waitingCount,
        cancelledCount,
        programStats
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        통계 데이터를 불러올 수 없습니다.
      </div>
    );
  }

  const totalApplications = stats.registeredCount + stats.waitingCount + stats.cancelledCount;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">통계</h1>
      </div>

      {/* 핵심 지표 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500 mb-1">총 수강생</p>
          <p className="text-3xl font-bold text-gray-900">{stats.totalStudents}<span className="text-lg font-normal text-gray-500 ml-1">명</span></p>
          <p className="text-xs text-gray-400 mt-2">이번달 +{stats.newStudentsThisMonth}명</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500 mb-1">운영 프로그램</p>
          <p className="text-3xl font-bold text-gray-900">{stats.activePrograms}<span className="text-lg font-normal text-gray-500 ml-1">개</span></p>
          <p className="text-xs text-gray-400 mt-2">전체 {stats.totalPrograms}개</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500 mb-1">총 결제액</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}<span className="text-lg font-normal text-gray-500 ml-1">원</span></p>
          <p className="text-xs text-gray-400 mt-2">미결제 {formatCurrency(stats.pendingPayments)}원</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500 mb-1">환불액</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.refundedAmount)}<span className="text-lg font-normal text-gray-500 ml-1">원</span></p>
          <p className="text-xs text-gray-400 mt-2">완료 건</p>
        </div>
      </div>

      {/* 하단 2컬럼 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 수강 상태 */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">수강 상태별 현황</h3>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">접수완료</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{stats.registeredCount}명</span>
                  <span className="text-sm text-gray-400 w-12 text-right">{totalApplications > 0 ? Math.round(stats.registeredCount / totalApplications * 100) : 0}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">대기</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{stats.waitingCount}명</span>
                  <span className="text-sm text-gray-400 w-12 text-right">{totalApplications > 0 ? Math.round(stats.waitingCount / totalApplications * 100) : 0}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">취소</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{stats.cancelledCount}명</span>
                  <span className="text-sm text-gray-400 w-12 text-right">{totalApplications > 0 ? Math.round(stats.cancelledCount / totalApplications * 100) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 프로그램별 수강생 */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">프로그램별 수강생</h3>
          </div>
          <div className="p-5">
            {stats.programStats.length > 0 ? (
              <div className="space-y-3">
                {stats.programStats.map((program, index) => (
                  <div key={program.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-gray-400 w-5">{index + 1}</span>
                      <span className="text-sm text-gray-900 truncate">{program.name}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-medium text-gray-900">{program.count}명</span>
                      <span className="text-sm text-gray-400 w-20 text-right">
                        {program.revenue > 0 ? `${formatCurrency(program.revenue)}원` : '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">데이터 없음</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
